import { IonButton, IonContent, IonHeader, IonIcon, IonItem, IonLabel, IonList, IonPage, IonSegment, IonSegmentButton, IonTitle, IonToolbar } from '@ionic/react';
import { chevronBack, chevronForward, today } from 'ionicons/icons';
import { useEffect, useMemo, useState } from 'react';
import { addClientListener } from '../firebase/clients';
import { useHistory } from 'react-router';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import * as moment from 'moment';
import 'moment/locale/pl';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './BirthdaysPage.css';

type ClientBirthdayEntry = {
  id: string;
  name: string;
  month: number;
  day: number;
};

type BirthdayEvent = {
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  clientId: string;
};

type WeekDayBirthdays = {
  date: Date;
  label: string;
  entries: Array<{ id: string; name: string }>;
};

moment.locale('pl');
const localizer = momentLocalizer(moment);

function isClientActive(value: any): boolean {
  if (value === false || value === 'false' || value === 0 || value === '0') {
    return false;
  }
  return true;
}

function parseBirthday(value: string): { month: number; day: number } | null {
  if (!value || typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  let month = Number.NaN;
  let day = Number.NaN;

  // Prefer YYYY-MM-DD (native Date/Firestore string style).
  const isoMatch = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoMatch) {
    month = parseInt(isoMatch[2], 10);
    day = parseInt(isoMatch[3], 10);
  } else {
    // Fallback for DD.MM or DD/MM.
    const dotMatch = normalized.match(/^(\d{1,2})[./](\d{1,2})/);
    if (dotMatch) {
      day = parseInt(dotMatch[1], 10);
      month = parseInt(dotMatch[2], 10);
    }
  }

  if (Number.isNaN(month) || Number.isNaN(day)) {
    return null;
  }

  const probe = new Date(2024, month - 1, day);
  if (probe.getMonth() !== month - 1 || probe.getDate() !== day) {
    return null;
  }

  return { month, day };
}

function getSafeBirthdayDate(year: number, month: number, day: number): Date {
  const maxDayInMonth = new Date(year, month, 0).getDate();
  return new Date(year, month - 1, Math.min(day, maxDayInMonth));
}

function getWeekStart(date: Date): Date {
  const weekStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const weekday = weekStart.getDay();
  const mondayOffset = weekday === 0 ? 6 : weekday - 1;
  weekStart.setDate(weekStart.getDate() - mondayOffset);
  return weekStart;
}

const BirthdaysPage: React.FC = () => {
  const history = useHistory();
  const [clients, setClients] = useState<any[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<any>(Views.MONTH);

  useEffect(() => {
    addClientListener((list: any[]) => {
      setClients(list ?? []);
    });
  }, []);

  const birthdayEntries = useMemo(() => {
    return clients
      .filter((client: any) => isClientActive(client?.active) && typeof client?.birthday === 'string' && client.birthday.trim() !== '')
      .map((client: any) => {
        const parsed = parseBirthday(client.birthday);
        if (!parsed) {
          return null;
        }

        return {
          id: client.id,
          name: `${client.surname ?? ''} ${client.name ?? ''}`.trim(),
          month: parsed.month,
          day: parsed.day,
        };
      })
      .filter((item: ClientBirthdayEntry | null): item is ClientBirthdayEntry => item !== null);
  }, [clients]);

  const birthdayEvents = useMemo(() => {
    const year = currentDate.getFullYear();
    return birthdayEntries
      .map((entry) => {
        const start = getSafeBirthdayDate(year, entry.month, entry.day);
        return {
          title: entry.name,
          start,
          end: start,
          allDay: true,
          clientId: entry.id,
        } as BirthdayEvent;
      })
      .sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [birthdayEntries, currentDate]);

  const periodLabel = useMemo(() => {
    if (currentView === Views.WEEK) {
      const weekStart = getWeekStart(currentDate);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const startLabel = weekStart.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });
      const endLabel = weekEnd.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });
      return `${startLabel} - ${endLabel}`;
    }
    return currentDate.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });
  }, [currentDate, currentView]);

  const todayDate = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const weekRows = useMemo(() => {
    if (currentView !== Views.WEEK) {
      return [] as WeekDayBirthdays[];
    }

    const weekStart = getWeekStart(currentDate);
    const rows: WeekDayBirthdays[] = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);

      const entries = birthdayEntries
        .filter((entry) => {
          const birthdayDate = getSafeBirthdayDate(date.getFullYear(), entry.month, entry.day);
          return birthdayDate.getMonth() === date.getMonth() && birthdayDate.getDate() === date.getDate();
        })
        .map((entry) => ({ id: entry.id, name: entry.name }))
        .sort((a, b) => a.name.localeCompare(b.name));

      rows.push({
        date,
        label: date.toLocaleDateString('pl-PL', { weekday: 'long', day: '2-digit', month: '2-digit' }),
        entries,
      });
    }

    return rows;
  }, [birthdayEntries, currentDate, currentView]);

  function nextPeriod() {
    if (currentView === Views.WEEK) {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 7));
      return;
    }
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  }

  function previousPeriod() {
    if (currentView === Views.WEEK) {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - 7));
      return;
    }
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  }

  function resetToToday() {
    setCurrentDate(new Date());
  }

  function onSelectBirthdayEvent(event: BirthdayEvent) {
    history.push('/app/clients/' + event.clientId);
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Urodziny</IonTitle>
        </IonToolbar>
        <IonSegment value={currentView} onIonChange={(e) => setCurrentView((e.detail.value as string) || Views.MONTH)}>
          <IonSegmentButton value={Views.MONTH}>
            <IonLabel>Miesiac</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value={Views.WEEK}>
            <IonLabel>Tydzien</IonLabel>
          </IonSegmentButton>
        </IonSegment>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '8px 12px' }}>
          <IonButton size="small" onClick={previousPeriod}>
            <IonIcon icon={chevronBack}></IonIcon>
          </IonButton>
          <IonButton size="small" color="light" onClick={resetToToday}>
            <IonIcon icon={today}></IonIcon>
          </IonButton>
          <IonLabel>{periodLabel}</IonLabel>
          <IonButton size="small" onClick={nextPeriod}>
            <IonIcon icon={chevronForward}></IonIcon>
          </IonButton>
        </div>
      </IonHeader>
      <IonContent fullscreen>
        {currentView === Views.MONTH ? (
          <div style={{ height: 'calc(100vh - 180px)', minHeight: '520px' }}>
            <Calendar
              className="birthdays-calendar"
              localizer={localizer}
              date={currentDate}
              onNavigate={(date) => setCurrentDate(date)}
              events={birthdayEvents}
              startAccessor="start"
              endAccessor="end"
              view={Views.MONTH}
              views={[Views.MONTH]}
              toolbar={false}
              popup
              messages={{
                month: 'Miesiac',
                week: 'Tydzien',
                day: 'Dzien',
                today: 'Dzis',
                previous: 'Poprzedni',
                next: 'Nastepny',
                agenda: 'Agenda',
                date: 'Data',
                time: 'Czas',
                event: 'Wydarzenie',
                noEventsInRange: 'Brak urodzin w tym zakresie',
                showMore: (total) => '+' + total,
              }}
              dayPropGetter={(date) => {
                const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                if (d.getTime() === todayDate.getTime()) {
                  return { style: { backgroundColor: '#fff4cc' } };
                }
                return {};
              }}
              eventPropGetter={(event) => {
                const d = new Date(event.start.getFullYear(), event.start.getMonth(), event.start.getDate());
                const isTodayBirthday = d.getTime() === todayDate.getTime();
                return {
                  style: {
                    backgroundColor: isTodayBirthday ? '#d84315' : '#2e7d32',
                    borderColor: isTodayBirthday ? '#d84315' : '#2e7d32',
                    color: 'white',
                    fontWeight: isTodayBirthday ? 700 : 500,
                  },
                };
              }}
              onSelectEvent={(event) => onSelectBirthdayEvent(event as BirthdayEvent)}
            />
          </div>
        ) : (
          <IonList className="birthdays-week-list">
            {weekRows.map((row) => {
              const isToday =
                row.date.getFullYear() === todayDate.getFullYear() &&
                row.date.getMonth() === todayDate.getMonth() &&
                row.date.getDate() === todayDate.getDate();
              return (
                <div key={row.label} className={isToday ? 'birthdays-week-row birthdays-week-row-today' : 'birthdays-week-row'}>
                  <div className="birthdays-week-day">{row.label}</div>
                  {row.entries.length === 0 ? (
                    <div className="birthdays-week-empty">Brak urodzin</div>
                  ) : (
                    row.entries.map((entry) => (
                      <IonItem key={entry.id} button onClick={() => history.push('/app/clients/' + entry.id)}>
                        <IonLabel>{entry.name}</IonLabel>
                      </IonItem>
                    ))
                  )}
                </div>
              );
            })}
          </IonList>
        )}
      </IonContent>
    </IonPage>
  );
};

export default BirthdaysPage;
